import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ITelemetryItem } from "@microsoft/applicationinsights-web";

let reactPlugin: ReactPlugin | null = null;
let appInsights: ApplicationInsights | null = null;

/**
 * Initialize Application Insights for Partner Portal
 * Call this function during app startup
 */
export const initializeApplicationInsights = (
  connectionString: string,
): { reactPlugin: ReactPlugin; appInsights: ApplicationInsights } | null => {
  console.log(
    "[ApplicationInsights] Initializing with connection string:",
    connectionString ? `${connectionString.substring(0, 30)}...` : "NONE",
  );

  if (
    !connectionString ||
    connectionString === "YOUR_CONNECTION_STRING_HERE" ||
    connectionString.startsWith("YOUR_") ||
    connectionString.startsWith("PLACEHOLDER_")
  ) {
    console.warn("[ApplicationInsights] Connection string not configured. Telemetry disabled.");
    console.log(
      "[ApplicationInsights] To enable telemetry, add a valid connection string to your .env file or config.js",
    );
    return null;
  }

  if (appInsights) {
    console.warn("[ApplicationInsights] Already initialized.");
    return { reactPlugin: reactPlugin!, appInsights };
  }

  try {
    console.log("[ApplicationInsights] Creating React plugin...");
    reactPlugin = new ReactPlugin();

    console.log("[ApplicationInsights] Configuring ApplicationInsights SDK...");
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true, // Automatically track route changes
        enableCorsCorrelation: true, // Enable CORS correlation for API calls
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        autoTrackPageVisitTime: true, // Track time spent on pages
        disableFetchTracking: false, // Track fetch requests
        disableAjaxTracking: false, // Track AJAX requests
        disableExceptionTracking: false, // Track exceptions
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: {
            history: undefined, // Will be set in App.tsx with React Router history
          },
        },
      },
    });

    console.log("[ApplicationInsights] Loading ApplicationInsights...");
    appInsights.loadAppInsights();

    // Add telemetry initializer to include custom dimensions in all telemetry
    appInsights.addTelemetryInitializer((envelope: ITelemetryItem): boolean => {
      // Set LogLevel based on event type
      const baseType = envelope.baseType || "";
      const logLevel = baseType.includes("Exception") ? "Error" : "Information";

      // Ensure baseData exists
      if (!envelope.data) {
        envelope.data = {};
      }
      if (!envelope.data.baseData) {
        envelope.data.baseData = {};
      }

      // Set custom properties - these get mapped to customDimensions
      if (!envelope.data.baseData.properties) {
        envelope.data.baseData.properties = {};
      }

      envelope.data.baseData.properties["ApplicationName"] = "PartnerPortalUI";
      envelope.data.baseData.properties["LogLevel"] = logLevel;

      // Also try setting them in tags for better compatibility
      if (!envelope.tags) {
        envelope.tags = {};
      }
      envelope.tags["ai.application.name"] = "PartnerPortalUI";

      return true;
    });

    console.log("[ApplicationInsights] Tracking initial page view...");
    appInsights.trackPageView(); // Track initial page view

    console.log("[ApplicationInsights] ✅ Successfully initialized and ready to track events!");
    console.log(
      "[ApplicationInsights] Features enabled: Page Views, API Tracking, Exception Tracking, Performance Monitoring",
    );
    return { reactPlugin, appInsights };
  } catch (error) {
    console.error("[ApplicationInsights] ❌ Failed to initialize:", error);
    return null;
  }
};

/**
 * Get the initialized Application Insights instance
 */
export const getAppInsights = (): ApplicationInsights | null => {
  return appInsights;
};

/**
 * Get the React Plugin instance
 */
export const getReactPlugin = (): ReactPlugin | null => {
  return reactPlugin;
};

/**
 * Track a custom event
 */
export const trackEvent = (name: string, properties?: Record<string, unknown>, measurements?: Record<string, number>): void => {
  if (appInsights) {
    const eventProperties = {
      ApplicationName: "PartnerPortalUI",
      LogLevel: "Information",
      timestamp: new Date().toISOString(),
      ...properties,
    };
    appInsights.trackEvent({ name, properties: eventProperties }, measurements);
  }
};

/**
 * Track an exception
 */
export const trackException = (error: Error, severityLevel?: number): void => {
  if (appInsights) {
    appInsights.trackException({
      exception: error,
      severityLevel,
      properties: {
        ApplicationName: "PartnerPortalUI",
        LogLevel: "Error",
        timestamp: new Date().toISOString(),
        errorMessage: error.message,
        errorStack: error.stack,
      },
    });
  }
};

/**
 * Track a page view manually
 */
export const trackPageView = (name?: string, uri?: string): void => {
  if (appInsights) {
    appInsights.trackPageView({
      name: name,
      uri: uri,
      properties: {
        ApplicationName: "PartnerPortalUI",
        LogLevel: "Information",
        timestamp: new Date().toISOString(),
      },
    } as any);
  }
};

/**
 * Track API request
 */
export const trackApiRequest = (
  name: string,
  url: string,
  duration: number,
  responseCode: number,
  success: boolean,
  properties?: Record<string, unknown>,
): void => {
  if (appInsights) {
    appInsights.trackDependencyData({
      id: `${Date.now()}-${Math.random()}`,
      name,
      duration,
      success,
      responseCode,
      type: "HTTP",
      target: url,
      data: url,
      properties,
    });
  }
};

/**
 * Set authenticated user context
 */
export const setAuthenticatedUserContext = (authenticatedUserId: string, accountId?: string): void => {
  if (appInsights) {
    appInsights.setAuthenticatedUserContext(authenticatedUserId, accountId, true);
  }
};

/**
 * Clear authenticated user context
 */
export const clearAuthenticatedUserContext = (): void => {
  if (appInsights) {
    appInsights.clearAuthenticatedUserContext();
  }
};

/**
 * Flush telemetry queue to ensure all events are sent
 */
export const flushTelemetry = (): void => {
  if (appInsights) {
    appInsights.flush();
  }
};

/**
 * Track a trace message
 */
export const trackTrace = (message: string, severityLevel?: number): void => {
  if (appInsights) {
    appInsights.trackTrace({
      message: message,
      severityLevel: severityLevel,
      properties: {
        ApplicationName: "PartnerPortalUI",
        LogLevel: "Information",
        timestamp: new Date().toISOString(),
      },
    } as any);
  }
};

/**
 * Track a custom metric
 */
export const trackMetric = (name: string, value: number, properties?: Record<string, unknown>): void => {
  if (appInsights) {
    appInsights.trackMetric({
      name: name,
      average: value,
      properties: {
        ApplicationName: "PartnerPortalUI",
        LogLevel: "Information",
        timestamp: new Date().toISOString(),
        ...properties,
      },
    } as any);
  }
};
